import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Megaphone, Send, Users, MessageSquare, MessageCircle, Plus, Loader2, Eye, Trash2,
  RefreshCw, X, Sparkles, CheckCircle2, AlertOctagon, ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import api from '../lib/api';

const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700',
  scheduled: 'bg-sky-100 text-sky-700',
  sent: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
};

export default function MarketingPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [opts, setOpts] = useState({ categories: [], genders: [], chronic_suggestions: [] });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, op] = await Promise.all([
        api.get('/marketing/campaigns'),
        api.get('/marketing/audience-options'),
      ]);
      setCampaigns(cs.data?.campaigns || []);
      setOpts(op.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load marketing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const removeDraft = async (id) => {
    if (!window.confirm('Delete this draft campaign?')) return;
    try {
      await api.delete(`/marketing/campaigns/${id}`);
      toast.success('Draft deleted');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh] text-muted-foreground">
      <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-5" data-testid="marketing-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Megaphone className="h-7 w-7 text-emerald-600" />
            Marketing
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Send targeted SMS or WhatsApp campaigns to patients by category, condition, age or activity.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={reload} className="gap-1.5" data-testid="campaigns-refresh-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="new-campaign-btn">
            <Plus className="h-4 w-4" /> New campaign
          </Button>
        </div>
      </header>

      {/* Empty state */}
      {campaigns.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-muted py-16 text-center" data-testid="campaigns-empty">
          <Megaphone className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-base font-semibold">No campaigns yet</p>
          <p className="text-sm text-muted-foreground mb-4">
            Reach the right patients at the right time by SMS or WhatsApp — chronic-care
            follow-ups, promo announcements, refill nudges.
          </p>
          <Button onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
            <Plus className="h-4 w-4" /> Create your first campaign
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase font-semibold tracking-wide text-muted-foreground border-b bg-muted/30">
            <div className="col-span-3">Campaign</div>
            <div className="col-span-3">Message</div>
            <div className="col-span-2 text-right">Recipients</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y">
            {campaigns.map((c) => (
              <div key={c.campaign_id} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm" data-testid={`campaign-${c.campaign_id}`}>
                <div className="col-span-3 min-w-0">
                  <p className="font-semibold truncate flex items-center gap-1.5">
                    {c.channel === 'whatsapp'
                      ? <MessageCircle className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      : <MessageSquare className="h-3.5 w-3.5 text-sky-600 shrink-0" />}
                    {c.name}
                  </p>
                  <p className="text-[11px] text-muted-foreground tabular-nums">
                    {c.status === 'scheduled' && c.scheduled_at
                      ? `Sends ${new Date(c.scheduled_at).toLocaleString()}`
                      : new Date(c.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="col-span-3 text-xs text-muted-foreground line-clamp-2">
                  {c.channel === 'whatsapp' ? `Template: ${c.template_name}` : c.message}
                </div>
                <div className="col-span-2 text-right tabular-nums">
                  <span className="font-semibold">{c.sent_count}</span>
                  <span className="text-muted-foreground">/{c.recipient_count || '?'}</span>
                  {c.failed_count > 0 && <p className="text-[10px] text-rose-600">{c.failed_count} failed</p>}
                </div>
                <div className="col-span-2">
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_TONE[c.status] || STATUS_TONE.draft}`}>
                    {c.status}
                  </span>
                </div>
                <div className="col-span-2 text-right flex items-center justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDetail(c)} data-testid={`view-${c.campaign_id}`}>
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  {(c.status === 'draft' || c.status === 'scheduled') && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => removeDraft(c.campaign_id)} data-testid={`del-${c.campaign_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CreateCampaignDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        opts={opts}
        onCreated={() => reload()}
      />
      <CampaignDetailDialog
        campaign={detail}
        onOpenChange={(o) => !o && setDetail(null)}
        onRefresh={reload}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Create campaign — multi-section form with live audience preview
// ──────────────────────────────────────────────────────────────────
function CreateCampaignDialog({ open, onOpenChange, opts, onCreated }) {
  const [channel, setChannel] = useState('sms');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [includeWA, setIncludeWA] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');  // <input type="datetime-local"> value
  const [filters, setFilters] = useState({
    categories: [], genders: [], chronic_conditions: [],
    age_min: '', age_max: '', visited_within_days: '', inactive_for_days: '',
    keyword: '',
  });
  const [chronicInput, setChronicInput] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateParams, setTemplateParams] = useState([]);

  useEffect(() => {
    if (!open) {
      setChannel('sms');
      setName(''); setMessage(''); setIncludeWA(false); setScheduledAt(''); setPreview(null);
      setFilters({ categories: [], genders: [], chronic_conditions: [], age_min: '', age_max: '', visited_within_days: '', inactive_for_days: '', keyword: '' });
      setChronicInput('');
      setTemplateName(''); setTemplateParams([]);
    } else {
      api.get('/whatsapp/templates').then((r) => {
        setTemplates(r.data.templates || []);
        setTemplatesError(r.data.ok === false ? (r.data.error || 'Could not load templates') : '');
      }).catch(() => setTemplatesError('Could not load templates'));
    }
  }, [open]);

  const selectedTemplate = useMemo(() => templates.find((t) => t.name === templateName), [templates, templateName]);
  const paramCount = useMemo(() => {
    const matches = [...((selectedTemplate?.body_text || '').matchAll(/\{\{\s*(\d+)\s*\}\}/g))];
    return matches.length ? Math.max(...matches.map((m) => +m[1])) : 0;
  }, [selectedTemplate]);

  useEffect(() => {
    setTemplateParams((prev) => {
      const next = Array.from({ length: paramCount }, (_, i) => prev[i] || '');
      return next;
    });
  }, [paramCount]);

  const audiencePayload = useMemo(() => {
    const a = { has_phone: true };
    if (filters.categories.length) a.categories = filters.categories;
    if (filters.genders.length) a.genders = filters.genders;
    if (filters.chronic_conditions.length) a.chronic_conditions = filters.chronic_conditions;
    if (filters.age_min !== '' && !Number.isNaN(+filters.age_min)) a.age_min = +filters.age_min;
    if (filters.age_max !== '' && !Number.isNaN(+filters.age_max)) a.age_max = +filters.age_max;
    if (filters.visited_within_days !== '' && !Number.isNaN(+filters.visited_within_days)) a.visited_within_days = +filters.visited_within_days;
    if (filters.inactive_for_days !== '' && !Number.isNaN(+filters.inactive_for_days)) a.inactive_for_days = +filters.inactive_for_days;
    if (filters.keyword.trim()) a.keyword = filters.keyword.trim();
    return a;
  }, [filters]);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post('/marketing/preview-audience', { audience: audiencePayload });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const toggleArr = (field, value) => {
    setFilters(f => ({
      ...f,
      [field]: f[field].includes(value) ? f[field].filter(x => x !== value) : [...f[field], value],
    }));
    setPreview(null);
  };

  const addChronic = () => {
    const v = chronicInput.trim().toLowerCase();
    if (!v) return;
    if (filters.chronic_conditions.includes(v)) { setChronicInput(''); return; }
    setFilters(f => ({ ...f, chronic_conditions: [...f.chronic_conditions, v] }));
    setChronicInput('');
    setPreview(null);
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (channel === 'whatsapp' ? !templateName : !message.trim()) {
      toast.error(channel === 'whatsapp' ? 'Pick an approved template' : 'Message is required');
      return;
    }
    if (channel === 'whatsapp' && templateParams.some((p) => !p.trim())) {
      toast.error('Fill in every template placeholder before saving');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        channel,
        audience: audiencePayload,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
      };
      if (channel === 'whatsapp') {
        payload.template_name = templateName;
        payload.template_language = selectedTemplate?.language || 'en';
        payload.template_params = templateParams;
      } else {
        payload.message = message.trim();
        payload.include_whatsapp_link = includeWA;
      }
      await api.post('/marketing/campaigns', payload);
      toast.success('Draft campaign saved');
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const charsLeft = 480 - (message.length + (includeWA ? 60 : 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-campaign-dialog">
        <DialogHeader>
          <DialogTitle>New {channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} campaign</DialogTitle>
          <DialogDescription>
            {channel === 'whatsapp'
              ? 'Pick an approved Meta template — WhatsApp does not allow free-form broadcast text.'
              : <>Tokens: <code className="text-xs">{`{{name}}`}</code>, <code className="text-xs">{`{{pharmacy}}`}</code>.</>}
            {' '}Drafts are saved — you can review before sending.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex rounded-lg border p-1 bg-muted/30 w-fit" data-testid="camp-channel-toggle">
            <button
              type="button" onClick={() => setChannel('sms')} data-testid="camp-channel-sms"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${channel === 'sms' ? 'bg-sky-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <MessageSquare className="h-3.5 w-3.5" /> SMS
            </button>
            <button
              type="button" onClick={() => setChannel('whatsapp')} data-testid="camp-channel-whatsapp"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${channel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </button>
          </div>

          <div>
            <Label>Campaign name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. October refill reminder" data-testid="camp-name-input" />
          </div>

          {channel === 'whatsapp' ? (
            <div className="rounded-lg border p-3 space-y-3 bg-muted/30">
              <div>
                <Label>Approved template</Label>
                <Select value={templateName} onValueChange={setTemplateName}>
                  <SelectTrigger className="h-9" data-testid="camp-template-select">
                    <SelectValue placeholder={templates.length ? 'Pick an approved template' : 'No approved templates found'} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.name} value={t.name}>{t.name} ({t.status})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {templatesError && <p className="text-[11px] text-red-500 mt-1" data-testid="camp-templates-error">{templatesError}</p>}
              </div>

              {selectedTemplate?.body_text && (
                <p className="text-xs text-muted-foreground italic rounded border bg-card px-2.5 py-2" data-testid="camp-template-preview">
                  "{selectedTemplate.body_text}"
                </p>
              )}

              {paramCount > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Fill in the {paramCount} placeholder{paramCount > 1 ? 's' : ''} — use <code className="text-[10px]">{`{{name}}`}</code> or <code className="text-[10px]">{`{{pharmacy}}`}</code> for per-patient values, or plain text
                  </Label>
                  {templateParams.map((val, i) => (
                    <Input
                      key={i} value={val} placeholder={`Placeholder {{${i + 1}}}`}
                      onChange={(e) => setTemplateParams((prev) => prev.map((p, idx) => (idx === i ? e.target.value : p)))}
                      className="h-8" data-testid={`camp-template-param-${i}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Message</Label>
                  <span className={`text-xs tabular-nums ${charsLeft < 20 ? 'text-rose-600' : 'text-muted-foreground'}`}>
                    {charsLeft} chars left
                  </span>
                </div>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Hi {{name}}, this is a friendly reminder from {{pharmacy}}…"
                  rows={4}
                  maxLength={480}
                  data-testid="camp-message-input"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                <div>
                  <Label className="font-semibold">Append WhatsApp click-to-chat link</Label>
                  <p className="text-xs text-muted-foreground">Adds <code>wa.me/...</code> URL — patients can reply on WhatsApp directly.</p>
                </div>
                <Switch checked={includeWA} onCheckedChange={setIncludeWA} data-testid="camp-include-wa" />
              </div>
            </>
          )}

          <div className="rounded-lg border p-3 space-y-1.5 bg-muted/30">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Schedule for later (optional)</Label>
              {scheduledAt && (
                <button type="button" className="text-[10px] text-muted-foreground hover:text-foreground" onClick={() => setScheduledAt('')}>
                  clear
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Leave blank to save as draft. Set a date/time to auto-send when the
              hourly scheduler ticks past it.
            </p>
            <Input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="h-9 max-w-xs"
              data-testid="camp-schedule-input"
            />
          </div>

          <div className="rounded-lg border bg-card p-4 space-y-3">
            <h3 className="text-sm font-bold tracking-tight flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-600" /> Audience targeting
            </h3>

            {/* Categories */}
            <div>
              <Label className="text-xs text-muted-foreground">Patient category</Label>
              <ChipPicker options={opts.categories} selected={filters.categories} onToggle={(v) => toggleArr('categories', v)} testid="cat" />
            </div>

            {/* Genders */}
            <div>
              <Label className="text-xs text-muted-foreground">Gender</Label>
              <ChipPicker options={opts.genders} selected={filters.genders} onToggle={(v) => toggleArr('genders', v)} testid="gen" />
            </div>

            {/* Chronic */}
            <div>
              <Label className="text-xs text-muted-foreground">Chronic condition / drug class (any keyword in medical history)</Label>
              <div className="flex gap-2">
                <Input value={chronicInput} onChange={(e) => setChronicInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChronic(); } }} placeholder="e.g. hypertension" className="h-8" data-testid="camp-chronic-input" />
                <Button type="button" variant="outline" size="sm" onClick={addChronic}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {filters.chronic_conditions.map(c => (
                  <Badge key={c} variant="secondary" className="gap-1 cursor-pointer" onClick={() => toggleArr('chronic_conditions', c)}>
                    {c} <X className="h-3 w-3" />
                  </Badge>
                ))}
                {opts.chronic_suggestions?.slice(0, 6).filter(s => !filters.chronic_conditions.includes(s)).map(s => (
                  <button key={s} type="button" className="text-[10px] text-muted-foreground hover:text-foreground border border-dashed rounded-full px-2 py-0.5" onClick={() => toggleArr('chronic_conditions', s)}>
                    + {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Age */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Min age</Label>
                <Input type="number" value={filters.age_min} onChange={(e) => { setFilters(f => ({ ...f, age_min: e.target.value })); setPreview(null); }} className="h-8" data-testid="camp-age-min" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Max age</Label>
                <Input type="number" value={filters.age_max} onChange={(e) => { setFilters(f => ({ ...f, age_max: e.target.value })); setPreview(null); }} className="h-8" data-testid="camp-age-max" />
              </div>
            </div>

            {/* Visit windows */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Visited in last (days)</Label>
                <Input type="number" value={filters.visited_within_days} onChange={(e) => { setFilters(f => ({ ...f, visited_within_days: e.target.value })); setPreview(null); }} className="h-8" data-testid="camp-recent" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Inactive for (days)</Label>
                <Input type="number" value={filters.inactive_for_days} onChange={(e) => { setFilters(f => ({ ...f, inactive_for_days: e.target.value })); setPreview(null); }} className="h-8" data-testid="camp-inactive" />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="flex items-center justify-between rounded-lg border bg-emerald-50/50 dark:bg-emerald-950/20 px-4 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-600" />
              <div>
                <p className="text-sm font-semibold">
                  {preview ? `${preview.recipient_count} matching patient${preview.recipient_count === 1 ? '' : 's'}` : 'Preview not run yet'}
                </p>
                {preview?.recipient_count > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    e.g. {preview.sample.slice(0, 3).map(s => s.name).filter(Boolean).join(', ') || 'samples hidden'}…
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={runPreview} disabled={previewing} data-testid="camp-preview-btn">
              {previewing && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Preview audience
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || !name.trim() || (channel === 'whatsapp' ? (!templateName || templateParams.some((p) => !p.trim())) : !message.trim())}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="camp-save-draft-btn"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            {scheduledAt ? 'Schedule campaign' : 'Save draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChipPicker({ options, selected, onToggle, testid }) {
  if (!options || options.length === 0) {
    return <p className="text-[11px] text-muted-foreground italic">No values found in patient roster.</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {options.map(o => {
        const on = selected.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onToggle(o)}
            data-testid={`camp-${testid}-${o}`}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors capitalize border ${
              on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-muted text-muted-foreground border-border hover:bg-muted/70'
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Detail dialog — shows full message + lets admin send
// ──────────────────────────────────────────────────────────────────
function CampaignDetailDialog({ campaign, onOpenChange, onRefresh }) {
  const [sending, setSending] = useState(false);
  const open = !!campaign;
  if (!open) return null;

  const send = async () => {
    if (!window.confirm(`Send "${campaign.name}" now? This cannot be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/marketing/campaigns/${campaign.campaign_id}/send`);
      toast.success(`Sent ${data.sent_count} of ${data.recipient_count} messages`);
      onRefresh && onRefresh();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const aud = campaign.audience || {};
  const audienceTags = [];
  if (aud.categories?.length) audienceTags.push(`Categories: ${aud.categories.join(', ')}`);
  if (aud.genders?.length) audienceTags.push(`Gender: ${aud.genders.join(', ')}`);
  if (aud.chronic_conditions?.length) audienceTags.push(`Conditions: ${aud.chronic_conditions.join(', ')}`);
  if (aud.age_min != null || aud.age_max != null) audienceTags.push(`Age ${aud.age_min ?? 0}-${aud.age_max ?? '∞'}`);
  if (aud.visited_within_days) audienceTags.push(`Visited last ${aud.visited_within_days}d`);
  if (aud.inactive_for_days) audienceTags.push(`Inactive ${aud.inactive_for_days}d+`);
  if (aud.keyword) audienceTags.push(`Keyword: ${aud.keyword}`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="campaign-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {campaign.channel === 'whatsapp'
              ? <MessageCircle className="h-5 w-5 text-emerald-600" />
              : <Megaphone className="h-5 w-5 text-emerald-600" />}
            {campaign.name}
          </DialogTitle>
          <DialogDescription>
            <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_TONE[campaign.status] || STATUS_TONE.draft}`}>
              {campaign.status}
            </span>
            {' · '}created {new Date(campaign.created_at).toLocaleString()}
            {campaign.status === 'scheduled' && campaign.scheduled_at && (
              <span className="text-sky-700 font-medium"> · scheduled {new Date(campaign.scheduled_at).toLocaleString()}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border bg-muted/30 p-3 text-sm whitespace-pre-wrap" data-testid="camp-message-display">
            {campaign.channel === 'whatsapp' ? (
              <>
                <span className="font-semibold">Template:</span> {campaign.template_name}
                {campaign.template_params?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">Placeholders: {campaign.template_params.join(' · ')}</p>
                )}
              </>
            ) : (
              <>
                {campaign.message}
                {campaign.include_whatsapp_link && (
                  <span className="text-xs text-emerald-600"> + WhatsApp link</span>
                )}
              </>
            )}
          </div>

          {audienceTags.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Audience</p>
              <div className="flex flex-wrap gap-1.5">
                {audienceTags.map(t => (
                  <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                ))}
              </div>
            </div>
          )}

          {campaign.status !== 'draft' && (
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Recipients" value={campaign.recipient_count} icon={Users} tone="slate" />
              <Stat label="Sent" value={campaign.sent_count} icon={CheckCircle2} tone="emerald" />
              <Stat label="Failed" value={campaign.failed_count} icon={AlertOctagon} tone={campaign.failed_count > 0 ? 'rose' : 'slate'} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Close</Button>
          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
            <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="camp-send-now-btn">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send now <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  const TONE = {
    slate:   'bg-slate-50 border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    rose:    'bg-rose-50 border-rose-200 text-rose-800',
  };
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${TONE[tone] || TONE.slate}`}>
      <Icon className="h-5 w-5 opacity-70" />
      <div>
        <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
        <p className="text-xl font-bold tabular-nums leading-none">{value}</p>
      </div>
    </div>
  );
}
